import { useEffect, useState } from 'react';
import { NAV_ITEMS, SITE } from '@/data/site.ts';
import { asset, href } from '@/lib/paths.ts';

/**
 * Fixed site header.
 *
 * `onLight` corresponds to the original `.nav--on-light` modifier used by the
 * article and policies pages, which have a light hero instead of a dark image
 * and therefore need dark nav text from the start.
 *
 * The original moved the `.nav__links` element into <body> with JS so the
 * fullscreen mobile menu could escape the nav's stacking context. That DOM
 * relocation is not reproducible in React (it would fight the reconciler), and
 * it is not needed: the overlay is fixed-position with z-index 10000 while the
 * nav bar itself creates no competing context. The backdrop is a sibling
 * element rather than a JS-injected div.
 */
export function Nav({
  currentPath,
  onLight = false,
}: {
  currentPath: string;
  onLight?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the fullscreen menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // Escape closes the menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const classes = [
    'nav',
    scrolled ? 'nav--scrolled' : '',
    onLight ? 'nav--on-light' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {menuOpen && <div className="nav__backdrop" aria-hidden="true" />}
      <nav className={classes} aria-label="Primary">
        <div className="nav__inner">
          <a
            href={href('/')}
            className="nav__logo"
            aria-label="Lux Financial — home"
          >
            <img
              src={asset(SITE.logo)}
              alt="Lux Financial"
              width={341}
              height={69}
              // Above the fold on every page — never lazy-load it.
              fetchPriority="high"
            />
          </a>

          <div
            id="nav-menu"
            className={`nav__links ${menuOpen ? 'nav__links--open' : ''}`.trim()}
            onClick={(e) => {
              // Clicking the overlay background (not a link) closes the menu.
              if (e.target === e.currentTarget) setMenuOpen(false);
            }}
          >
            {NAV_ITEMS.map((item) => {
              const active = item.href === currentPath;
              return (
                <a
                  key={item.href}
                  href={href(item.href)}
                  className={`nav__link ${active ? 'nav__link--active' : ''}`.trim()}
                  {...(active ? { 'aria-current': 'page' as const } : {})}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              );
            })}
            <a
              href={href('/#contact')}
              className="nav__link nav__link--mobile-only"
              onClick={() => setMenuOpen(false)}
            >
              Contact Us
            </a>
          </div>

          <div className="nav__right">
            <a
              href={href('/#contact')}
              className="nav__link nav__link--contact"
            >
              Contact Us
            </a>
            <a
              href={SITE.clientLogin}
              className="nav__cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Login</span>
            </a>
            <button
              type="button"
              className={`nav__toggle ${menuOpen ? 'nav__toggle--active' : ''}`.trim()}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="nav-menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}
